/**
 * Pastilak — Magnetic Button Engine
 * ----------------------------------
 * Any element with [data-magnetic] gets a cursor-attraction effect:
 * once the mouse enters an invisible zone around the button, the
 * button drifts toward the cursor. Leave the zone -> it springs back.
 *
 * Usage: add data-magnetic to any button/link.
 *   <button class="pk-btn" data-magnetic>Add to Bag</button>
 *
 * Optional tuning attributes:
 *   data-magnetic-radius="90"     px zone around the element (default 90)
 *   data-magnetic-strength="0.35" 0–1, how far it travels toward cursor (default 0.35)
 *   data-magnetic-scale="1.04"    slight scale-up while active (default 1.04, set to 1 to disable)
 *
 * Drop this file in /assets and reference it once, site-wide, e.g. in
 * theme.liquid:
 *   <script src="{{ 'pk-magnetic.js' | asset_url }}" defer></script>
 *
 * It auto-discovers elements on load AND watches the DOM for new ones
 * (quick-add buttons injected via AJAX, section re-renders, etc.)
 */

(function () {
  'use strict';

  const DEFAULT_RADIUS = 90;
  const DEFAULT_STRENGTH = 0.35;
  const DEFAULT_SCALE = 1.04;

  // Respect reduced-motion preference — just skip the effect entirely.
  const prefersReducedMotion = window.matchMedia(
    '(prefers-reduced-motion: reduce)'
  ).matches;

  class PKMagneticField {
    constructor() {
      this.items = new Map(); // el -> { rect, radius, strength, scale, active, rafId }
      this.mouse = { x: -9999, y: -9999 };
      this.ticking = false;

      if (prefersReducedMotion) return;

      this._onMouseMove = this._onMouseMove.bind(this);
      this._onScrollOrResize = this._onScrollOrResize.bind(this);
      this._tick = this._tick.bind(this);

      window.addEventListener('mousemove', this._onMouseMove, { passive: true });
      window.addEventListener('scroll', this._onScrollOrResize, { passive: true });
      window.addEventListener('resize', this._onScrollOrResize);
      // Images (esp. lazy-loaded ones) can finish loading after our
      // initial registration and shift layout below the fold — force
      // one more rect refresh once everything has settled.
      window.addEventListener('load', this._onScrollOrResize);

      this._observeDom();
      this.refresh();
      requestAnimationFrame(this._tick);
    }

    refresh() {
      const els = document.querySelectorAll('[data-magnetic]');
      els.forEach((el) => {
        if (this.items.has(el)) return; // already tracked
        this._register(el);
      });
    }

    _register(el) {
      el.classList.add('pk-magnetic');

      // Wrap the visual content in an inner span we can transform,
      // so the element's own layout/click box never moves — only
      // the visible label/pill shifts. This avoids messing with
      // hit-testing or surrounding layout.
      if (!el.querySelector(':scope > .pk-magnetic__inner')) {
        const inner = document.createElement('span');
        inner.className = 'pk-magnetic__inner';
        while (el.firstChild) inner.appendChild(el.firstChild);
        el.appendChild(inner);
      }

      const radius = parseFloat(el.dataset.magneticRadius) || DEFAULT_RADIUS;
      const strength =
        parseFloat(el.dataset.magneticStrength) || DEFAULT_STRENGTH;
      const scale = parseFloat(el.dataset.magneticScale) || DEFAULT_SCALE;

      this.items.set(el, {
        rect: el.getBoundingClientRect(),
        radius,
        strength,
        scale,
        active: false,
      });
    }

    _onMouseMove(e) {
      this.mouse.x = e.clientX;
      this.mouse.y = e.clientY;
    }

    _onScrollOrResize() {
      // Cheap debounce via rAF flag
      if (this.ticking) return;
      this.ticking = true;
      requestAnimationFrame(() => {
        this.items.forEach((data, el) => {
          data.rect = el.getBoundingClientRect();
        });
        this.ticking = false;
      });
    }

    _tick() {
      this.items.forEach((data, el) => {
        // Re-measure every frame rather than trusting the cached rect —
        // lazy-loaded images and late layout shifts (e.g. below-the-fold
        // sections) can move the element after registration/scroll/resize,
        // leaving the cached rect stale and the zone misaligned with the
        // visible button.
        const rect = el.getBoundingClientRect();
        data.rect = rect;
        const cx = rect.left + rect.width / 2;
        const cy = rect.top + rect.height / 2;

        const dx = this.mouse.x - cx;
        const dy = this.mouse.y - cy;
        const dist = Math.sqrt(dx * dx + dy * dy);

        const zone = data.radius + Math.max(rect.width, rect.height) / 2;
        const inner = el.querySelector('.pk-magnetic__inner');
        if (!inner) return;

        if (dist < zone) {
          if (!data.active) {
            data.active = true;
            el.classList.add('is-magnetic-active');
          }
          // Falloff: strongest at center of zone, ~0 at the edge.
          const pull = 1 - dist / zone;
          const tx = dx * data.strength * pull;
          const ty = dy * data.strength * pull;
          inner.style.transform = `translate(${tx}px, ${ty}px) scale(${
            1 + (data.scale - 1) * pull
          })`;
        } else if (data.active) {
          data.active = false;
          el.classList.remove('is-magnetic-active');
          inner.style.transform = '';
        }
      });

      requestAnimationFrame(this._tick);
    }

    _observeDom() {
      const observer = new MutationObserver(() => this.refresh());
      observer.observe(document.body, { childList: true, subtree: true });
    }
  }

  function init() {
    window.__pkMagneticField = new PKMagneticField();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();